/**
 * CrateBrowser Component
 * Browse and select crates with deck status
 */

import React, { useCallback } from 'react';
import { Box, Loader2 } from 'lucide-react';
import { CrateItem } from './CrateItem';
import type { CrateSummary } from '../../api/types';

interface CrateBrowserProps {
  searchQuery: string;
}

// Mock data for now - will be replaced with actual API hook
const MOCK_CRATES: CrateSummary[] = [
  {
    name: 'House Classics',
    poolSize: 150,
    rotationMode: 'SHUFFLE',
    searchDepthPct: 25,
    deckRemaining: 112,
  },
  {
    name: 'Deep Tech',
    poolSize: 200,
    rotationMode: 'FIFO',
    searchDepthPct: 30,
    deckRemaining: 187,
  },
  {
    name: 'Peak Hour Bangers',
    poolSize: 80,
    rotationMode: 'SHUFFLE',
    searchDepthPct: 20,
    deckRemaining: 38,
  },
  {
    name: 'Warm Up',
    poolSize: 120,
    rotationMode: 'FIFO',
    searchDepthPct: 25,
    deckRemaining: 95,
  },
  {
    name: 'Melodic Techno',
    poolSize: 180,
    rotationMode: 'SHUFFLE',
    searchDepthPct: 35,
    deckRemaining: 156,
  },
];

export function CrateBrowser({ searchQuery }: CrateBrowserProps) {
  // TODO: Replace with actual API hook when ready
  // const { data, isLoading, error } = useCrates();

  const isLoading = false;
  const error = null;

  // Filter crates by search query
  const filteredCrates = React.useMemo(() => {
    const normalizedQuery = searchQuery.toLowerCase().trim();
    return normalizedQuery
      ? MOCK_CRATES.filter(c => c.name.toLowerCase().includes(normalizedQuery))
      : MOCK_CRATES;
  }, [searchQuery]);

  // Handle crate selection
  const handleCrateClick = useCallback((crate: CrateSummary) => {
    console.log('Selected crate:', crate.name);
    // TODO: Navigate to crate detail view or open modal
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mb-3" />
        <p className="text-sm text-zinc-400">Loading crates...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center p-4">
        <Box className="w-12 h-12 text-red-500/50 mb-3" />
        <p className="text-sm text-red-400 mb-1">Failed to load crates</p>
        <p className="text-xs text-zinc-500">Unknown error</p>
      </div>
    );
  }

  // Empty state
  if (filteredCrates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center p-4">
        <Box className="w-12 h-12 text-zinc-700 mb-3" />
        {searchQuery ? (
          <>
            <p className="text-sm text-zinc-400 mb-1">No crates found</p>
            <p className="text-xs text-zinc-600">Try a different search term</p>
          </>
        ) : (
          <>
            <p className="text-sm text-zinc-400 mb-1">No crates yet</p>
            <p className="text-xs text-zinc-600">Create crates from catalogs to get started</p>
          </>
        )}
      </div>
    );
  }

  // Crate list
  return (
    <div className="flex-1 overflow-auto p-4 space-y-2">
      {filteredCrates.map((crate) => (
        <CrateItem key={crate.name} crate={crate} onClick={handleCrateClick} />
      ))}
    </div>
  );
}
